// ---------------------------------------------------------
// PatDistillationReviewPanel.jsx
// LLM distillation surface → explicit human acknowledgement
// | NO inference | NO auto-progression
// ---------------------------------------------------------

import React, { useEffect, useState } from "react";

function normalisePartition(partition) {
  const base =
    partition && typeof partition === "object"
      ? JSON.parse(JSON.stringify(partition))
      : { X: [], M: [], Z: [], Y: "" };

  return {
    X: Array.isArray(base.X) ? base.X : [],
    M: Array.isArray(base.M) ? base.M : [],
    Z: Array.isArray(base.Z) ? base.Z : [],
    Y: typeof base.Y === "string" ? base.Y : "",
  };
}

function formatRoleHeading(role) {
  const map = {
    X: "X - Candidate Cause / Exposure / Intervention Variable(s)",
    M: "M - Pathway or Mediator Variable(s)",
    Z: "Z - Background or Confounding Variable(s)",
    Y: "Y - Outcome",
  };

  return map[role] || role;
}

function roleHeadingClass(role) {
  const map = {
    X: "text-cyan-300",
    M: "text-cyan-300",
    Z: "text-cyan-300",
    Y: "text-violet-300",
  };

  return map[role] || "text-slate-300";
}

export default function PatDistillationReviewPanel({
  patDistillation,
  onAcknowledge,
}) {
  // HARD GUARD — render ONLY when distillation object exists
  if (!patDistillation) return null;

  // Allowed distillation statuses that require human review
  const allowedStatuses = [
    "multiple_outcomes_detected",
    "ready_for_pre_dag_workspace",
  ];

  if (!allowedStatuses.includes(patDistillation.status)) {
    return null;
  }

  const { original_question, notes, suggested_partition, constraints } =
    patDistillation;

  const [acknowledged, setAcknowledged] = useState(false);

  const [workingPartition, setWorkingPartition] = useState(() =>
    normalisePartition(suggested_partition),
  );

  useEffect(() => {
    setWorkingPartition(normalisePartition(suggested_partition));
  }, [JSON.stringify(suggested_partition || {})]);

  const addVariable = (role) => {
    setWorkingPartition((prev) => {
      const next = {
        X: [...(prev.X || [])],
        M: [...(prev.M || [])],
        Z: [...(prev.Z || [])],
        Y: typeof prev.Y === "string" ? prev.Y : "",
      };

      if (role === "Y") {
        next.Y = "";
      } else {
        next[role] = [...next[role], ""];
      }

      return next;
    });
  };

  const deleteVariable = (role, index) => {
    setWorkingPartition((prev) => {
      const next = {
        X: [...(prev.X || [])],
        M: [...(prev.M || [])],
        Z: [...(prev.Z || [])],
        Y: typeof prev.Y === "string" ? prev.Y : "",
      };

      if (role === "Y") {
        next.Y = "";
      } else {
        next[role] = next[role].filter((_, i) => i !== index);
      }

      return next;
    });
  };

  const moveVariable = (fromRole, index, toRole) => {
    setWorkingPartition((prev) => {
      const next = {
        X: [...(prev.X || [])],
        M: [...(prev.M || [])],
        Z: [...(prev.Z || [])],
        Y: typeof prev.Y === "string" ? prev.Y : "",
      };

      const item =
        fromRole === "Y" ? next.Y : (next[fromRole] || [])[index] || "";

      if (fromRole === "Y") {
        next.Y = "";
      } else {
        next[fromRole] = next[fromRole].filter((_, i) => i !== index);
      }

      if (toRole === "Y") {
        next.Y = item;
      } else if (item) {
        next[toRole] = [...next[toRole], item];
      }

      return next;
    });
  };

  const updateVariable = (role, index, value) => {
    setWorkingPartition((prev) => {
      const next = {
        X: [...(prev.X || [])],
        M: [...(prev.M || [])],
        Z: [...(prev.Z || [])],
        Y: typeof prev.Y === "string" ? prev.Y : "",
      };

      if (role === "Y") {
        next.Y = value;
      } else {
        next[role][index] = value;
      }

      return next;
    });
  };

  // --------------------------------------------------
  // Review affordances (UI-only, non-inferential)
  // --------------------------------------------------
  const review_actions = {
    allows_proceed_to_pre_dag:
      constraints?.human_confirmation_required === true,

    allows_rephrase: true, // always permitted at distillation stage

    allows_abort: true, // user may exit without committing variables
  };

  return (
    <div className="rounded-lg border border-amber-600 bg-slate-900 p-5 space-y-6">
      <h3 className="text-lg font-semibold text-amber-400">
        Review provisional variable structure
      </h3>

      {/* Original question (single authoritative rendering) */}
      {original_question && (
        <div className="space-y-1">
          <div className="text-xs text-slate-400">Input under review</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap">
            {original_question}
          </div>
        </div>
      )}

      {/* Distillation notes */}
      {notes && <div className="text-xs text-slate-400 italic">{notes}</div>}

      {/* Distilled semantic candidates */}
      {suggested_partition && (
        <div className="space-y-4">
          <div className="text-sm font-semibold text-slate-300">
            Provisional variable structure
          </div>

          {["Y", "X", "M", "Z"].map((role) => {
            const items =
              role === "Y"
                ? workingPartition.Y
                  ? [workingPartition.Y]
                  : []
                : workingPartition[role] || [];

            return (
              <div key={role} className="space-y-1">
                <div
                  className={`text-base font-semibold mb-2 ${roleHeadingClass(role)}`}
                >
                  {formatRoleHeading(role)}
                </div>

                {items.length === 0 && (
                  <div className="text-xs text-slate-500 italic">
                    (none surfaced at this checkpoint)
                  </div>
                )}

                <ul className="space-y-1">
                  {items.map((item, i) => (
                    <li
                      key={`${role}-${i}`}
                      className="flex items-center gap-2"
                    >
                      <select
                        value={role}
                        onChange={(e) => moveVariable(role, i, e.target.value)}
                        className="bg-slate-800 border border-slate-700 text-xs rounded px-1"
                      >
                        <option value="X">X</option>
                        <option value="M">M</option>
                        <option value="Z">Z</option>
                        <option value="Y">Y</option>
                      </select>

                      <input
                        value={item}
                        onChange={(e) =>
                          updateVariable(role, i, e.target.value)
                        }
                        placeholder={`Add ${role} variable`}
                        className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200"
                      />

                      <button
                        type="button"
                        onClick={() => deleteVariable(role, i)}
                        className="text-xs text-slate-400 hover:text-slate-200"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => addVariable(role)}
                  className="text-xs text-sky-400 hover:underline mt-1"
                >
                  + add
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Constraints reminder */}
      {constraints && (
        <div className="border-t border-slate-700 pt-3 space-y-1">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">
            Constraints in effect
          </div>
          <ul className="list-disc list-inside text-xs text-slate-400 ml-2">
            {constraints.exactly_one_outcome_required && (
              <li>Exactly one outcome (Y) is required</li>
            )}
            {constraints.no_causal_claims && (
              <li>No causal conclusion or intervention claim has been made</li>
            )}
            {constraints.human_confirmation_required && (
              <li>Human confirmation is required to proceed</li>
            )}
          </ul>
        </div>
      )}

      {/* Explicit acknowledgement gate */}
      <div className="border-t border-slate-700 pt-4 space-y-4">
        <label className="flex items-start gap-2 text-xs text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>
            I confirm that this checkpoint reasonably reflects the current
            provisional variable structure
          </span>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() =>
              onAcknowledge({
                partition: workingPartition,
              })
            }
            disabled={!acknowledged}
            className="px-4 py-2 text-xs rounded bg-amber-500 text-slate-900 font-semibold disabled:opacity-40"
          >
            {patDistillation.status === "multiple_outcomes_detected"
              ? "Confirm and continue"
              : "Confirm and continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
